import './social-network-workspace.ajscss';

import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { optToPromise } from '@commons/util/utilities/option';
import { IComponentController, IComponentOptions, IDeferred, ILogService, IPromise, IQService } from 'angular';
import { i18n } from 'i18next';
import { filter, Subscription } from 'rxjs';

import { BookingStatus, ChannelType, None, Option } from '@contact/upgrade';

import {
  ISocialNetworkFrameService,
  SOCIAL_NETWORK_CONTAINER_ID,
  SOCIAL_NETWORK_FRAME_NAME,
} from '../../services/social-network-frame.service';
import templateUrl from './social-network-workspace.template.html';

export interface ISocialNetworkFrame {
  show(responseId: string): void;

  hide(responseId: string): void;
}

export class SocialNetworkFrame implements ISocialNetworkFrame {
  constructor(private readonly socialNetworkFrameService: ISocialNetworkFrameService) {}

  show(responseId: string): void {
    this.socialNetworkFrameService.show(responseId);
  }

  hide(responseId: string): void {
    this.socialNetworkFrameService.hide(responseId);
  }
}

const MODULE_NAME = 'socialNetworkWorkspace';

class SocialNetworkWorkspaceCtrl implements IComponentController {
  responseId: string;
  private urlToken: string;
  private closeDeferred: IDeferred<any> = this.$q.defer();
  private socialNetworkFrame: Option<IDeferred<ISocialNetworkFrame>> = None;
  private subscriptions: Subscription[] = [];

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    public readonly $i18next: i18n,
    public readonly socialNetworkFrameService: ISocialNetworkFrameService,
    private readonly externalAppService: AppExternalService,
    private readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly contactDeprecatedService: IContactDeprecatedService
  ) {}

  $onInit(): void {
    if (!this.responseId) {
      return;
    }
    const snModel = this.socialNetworkFrameService.getSocialNetworkByResponseId(this.responseId);
    if (snModel) {
      this.socialNetworkFrameService.selectSocialNetwork(snModel);
    }
  }

  $postLink(): void {
    let frame = this.externalAppService.getApplicationContainer(SOCIAL_NETWORK_FRAME_NAME + '_' + this.responseId).getOrNull();
    if (frame) {
      setTimeout(() => {
        const container = document.getElementById(SOCIAL_NETWORK_CONTAINER_ID);
        if (container) {
          this.externalAppService.positionApplication(SOCIAL_NETWORK_FRAME_NAME + '_' + this.responseId, container);
          this.externalAppService.showApplication(SOCIAL_NETWORK_FRAME_NAME + '_' + this.responseId);
        }
      }, 0);
    } else {
      this.open();
    }
  }

  $onDestroy(): void {
    this.externalAppService.hideApplication(SOCIAL_NETWORK_FRAME_NAME + '_' + this.responseId);
  }

  open(): void {
    this.openFrame().then(frame => frame.show(this.responseId));
  }

  openFrame(): IPromise<ISocialNetworkFrame> {
    if (this.socialNetworkFrame.isEmpty()) {
      this.socialNetworkFrame = Option.of(this.$q.defer());
    } else {
      return this.socialNetworkFrame.get().promise;
    }

    this.$log.info(`[dimelo] Opening social network...`);

    this.subscriptions.push(
      this.contactDeprecatedService
        .contactState$()
        .pipe(
          filter(event => event.contact.responseId === this.responseId),
          filter(event => event.contact.channel.type === ChannelType.SOCIAL_NETWORK),
          filter(event => event.state === BookingStatus.ALLOCATED)
        )
        .subscribe(() => this.openFrame())
    );

    this.subscriptions.push(
      this.contactDeprecatedService
        .contactState$()
        .pipe(
          filter(event => event.contact.responseId === this.responseId),
          filter(event => event.contact.channel.type === ChannelType.SOCIAL_NETWORK),
          filter(event => event.state === BookingStatus.CLOSED || event.state === BookingStatus.POST_PROCESSING)
        )
        .subscribe(() => this.closeFrame())
    );

    if (this.contactDeprecatedService.getContactsByChannelType(ChannelType.SOCIAL_NETWORK).length > 0) {
      this.socialNetworkFrameService.getSocialNetworkContactUrl().then(result => {
        this.urlToken = result.data;
        this.loadFrame();
      });
    }

    return this.socialNetworkFrame.get().promise.catch(() => {
      this.toastDeprecatedService.alert('agent:toast.social_network_open_error');
      this.socialNetworkFrame = None;
      return this.$q.reject();
    });
  }

  loadFrame(): void {
    if (!!this.responseId) {
      optToPromise(this.externalAppService.createApplication(SOCIAL_NETWORK_FRAME_NAME + '_' + this.responseId), this.$q)
        .then(frame => {
          if (this.urlToken) {
            return this.contactDeprecatedService.getSelectedContact().then(contact => {
              let url = this.urlToken + 'content_threads/' + contact.context['dimelo_thread_id'] + '?view=permalink';
              return this.loadUrl(frame, url);
            });
          }
          return this.$q.reject();
        })
        .then(
          () => {
            this.$log.info(`[dimelo] Social network opened.`);
            this.closeDeferred = this.$q.defer();
            this.socialNetworkFrame.forEach(socialNetworkFrame =>
              socialNetworkFrame.resolve(new SocialNetworkFrame(this.socialNetworkFrameService))
            );
          },
          () => {
            this.toastDeprecatedService.alert('agent:toast.social_network_login_failed');
            this.socialNetworkFrame.forEach(socialNetworkFrame => socialNetworkFrame.reject());
          }
        );
    }
  }

  closeFrame(): void {
    this.get().then(frame => frame.hide(this.responseId));

    let container = this.externalAppService.getApplicationContainer(SOCIAL_NETWORK_FRAME_NAME + '_' + this.responseId);
    optToPromise(container, this.$q)
      .then(
        () => {
          this.$log.info('[dimelo] Service logged out.');
          this.closeDeferred.resolve();
        },
        () => this.closeDeferred.reject()
      )
      .finally(() => {
        this.socialNetworkFrame = None;
        this.externalAppService.removeApplication(SOCIAL_NETWORK_FRAME_NAME + '_' + this.responseId);
        this.$log.info('[dimelo] Social network closed.');
      });
  }

  private loadUrl(frame: HTMLIFrameElement, url: string): IPromise<HTMLIFrameElement> {
    let deferred = this.$q.defer<HTMLIFrameElement>();

    let listener = () => {
      frame.removeEventListener('load', listener);
      this.$log.debug('[dimelo] Frame loaded !');
      if (document.getElementsByClassName('user-form').length > 0) {
        this.$log.error('[dimelo] Login failed.');
        deferred.reject();
      } else {
        deferred.resolve(frame);
      }
    };
    frame.addEventListener('load', listener);
    this.$log.info(`[dimelo] Loading URL[${url}]`);
    frame.src = url;
    return deferred.promise;
  }

  get(): IPromise<ISocialNetworkFrame> {
    return this.socialNetworkFrame.map(frame => frame.promise).getOrElseFunc(() => this.$q.reject());
  }
}

export class SocialNetworkWorkspace implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    responseId: '<',
  };
  controller = [
    '$log',
    '$q',
    '$i18next',
    'socialNetworkFrameService',
    'externalApplications',
    'toastDeprecatedService',
    'contactDeprecatedService',
    SocialNetworkWorkspaceCtrl,
  ];
}

export default MODULE_NAME;
